/**
 * The app's time zones (docs/specs/PREFERENCES.md, Time Zones):
 *
 * - A person's Primary Time Zone (a Preference): where they usually work.
 *   Moments always show in it.
 * - The organization's zone: Primary Time Zone's default (Preference
 *   Defaults), the zone new schedules start in.
 * - Where they are now: their browser's zone, never stored. A moment also
 *   shows there when it differs (`formatMoment`).
 *
 * Everything here is pure: the zones come in as IANA names.
 */

/** Migration 0065's default for the organization's zone; stands in until configuration arrives. */
export const DEFAULT_TIME_ZONE = 'America/New_York'

const FORMAT_LOCALE = 'en-US'

const formatterCache = new Map<string, Intl.DateTimeFormat>()

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone}|${JSON.stringify(options)}`
  let cached = formatterCache.get(key)
  if (!cached) {
    cached = new Intl.DateTimeFormat(FORMAT_LOCALE, { ...options, timeZone })
    formatterCache.set(key, cached)
  }
  return cached
}

/** The browser's IANA zone: where the viewer is right now. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const canonicalCache = new Map<string, string | null>()

/** The engine's own spelling of a zone (`Asia/Kolkata` may read `Asia/Calcutta`), or null for none. */
function canonicalTimeZone(value: string): string | null {
  let canonical = canonicalCache.get(value)
  if (canonical === undefined) {
    try {
      canonical = new Intl.DateTimeFormat(FORMAT_LOCALE, { timeZone: value }).resolvedOptions()
        .timeZone
    } catch {
      canonical = null
    }
    canonicalCache.set(value, canonical)
  }
  return canonical
}

/** Whether `value` names an IANA time zone this browser knows. */
export function isTimeZone(value: unknown): value is string {
  // A zone name has a letter first; this also keeps out offsets such as "+05:00".
  return typeof value === 'string' && /^[A-Za-z]/.test(value) && canonicalTimeZone(value) !== null
}

/** Whether two zone names are the same zone (aliases included). */
export function isSameTimeZone(left: string, right: string): boolean {
  if (left === right) return true
  const canonicalLeft = canonicalTimeZone(left)
  return canonicalLeft !== null && canonicalLeft === canonicalTimeZone(right)
}

/** Every zone this browser offers, UTC first, plus `include` when it isn't one of them. */
export function listTimeZones(include: readonly string[] = []): string[] {
  const supportedValuesOf = (Intl as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf
  let zones: string[] = []
  try {
    zones = supportedValuesOf ? supportedValuesOf('timeZone') : []
  } catch {
    zones = []
  }
  if (zones.length === 0) zones = [browserTimeZone(), DEFAULT_TIME_ZONE]
  const listed = ['UTC', ...zones.filter((zone) => zone !== 'UTC')]
  for (const zone of include) {
    if (zone && !listed.some((candidate) => isSameTimeZone(candidate, zone))) listed.push(zone)
  }
  return listed
}

// ======================================================
// Wall clocks
// ======================================================

/** A moment as a wall clock somewhere. `month` is 1-12; `weekday` 0 is Sunday. */
export interface ZonedParts {
  year: number
  month: number
  day: number
  hours: number
  minutes: number
  seconds: number
  weekday: number
}

const PARTS_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  weekday: 'short',
  hourCycle: 'h23',
}
const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/** What a clock in `timeZone` reads at `date`. */
export function zonedParts(date: Date, timeZone: string): ZonedParts {
  const parts: Record<string, string> = {}
  for (const part of formatter(timeZone, PARTS_OPTIONS).formatToParts(date)) {
    parts[part.type] = part.value
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Some engines write midnight as 24 even in h23.
    hours: Number(parts.hour) % 24,
    minutes: Number(parts.minute),
    seconds: Number(parts.second),
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
  }
}

/** Minutes `timeZone` is ahead of UTC at `date` (New York in summer: -240). */
export function timeZoneOffsetMinutes(date: Date, timeZone: string): number {
  const parts = zonedParts(date, timeZone)
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds
  )
  return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / 60_000)
}

const pad = (value: number) => String(value).padStart(2, '0')

/** A wall clock's date as `YYYY-MM-DD`. */
export function zonedIsoDate(parts: Pick<ZonedParts, 'year' | 'month' | 'day'>): string {
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

/** Today's date in `timeZone`, as `YYYY-MM-DD`. */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): string {
  return zonedIsoDate(zonedParts(now, timeZone))
}

/** Minutes since midnight on a clock in `timeZone`; read with `todayInTimeZone` from one `Date`. */
export function minutesNowInTimeZone(timeZone: string, now: Date = new Date()): number {
  const parts = zonedParts(now, timeZone)
  return parts.hours * 60 + parts.minutes
}

const DAY_MS = 86_400_000

/**
 * The moment a clock in `timeZone` reads `minutesOfDay` on `isoDate`. Like
 * the engine (`schedule.py`, `fold=0`), a time that happens twice when clocks
 * go back is the first, and one skipped when they go forward reads on the
 * clock from before the change (2:30 AM becomes 3:30 AM).
 */
export function zonedTimeToDate(isoDate: string, minutesOfDay: number, timeZone: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number)
  const asUtc = Date.UTC(year, month - 1, day) + minutesOfDay * 60_000
  // Offsets run from -12 to +14 hours, so a day either side brackets the moment.
  const before = timeZoneOffsetMinutes(new Date(asUtc - DAY_MS), timeZone)
  const after = timeZoneOffsetMinutes(new Date(asUtc + DAY_MS), timeZone)
  const withBefore = asUtc - before * 60_000
  if (before === after || timeZoneOffsetMinutes(new Date(withBefore), timeZone) === before) {
    return new Date(withBefore)
  }
  const withAfter = asUtc - after * 60_000
  return timeZoneOffsetMinutes(new Date(withAfter), timeZone) === after
    ? new Date(withAfter)
    : new Date(withBefore)
}

// ======================================================
// Names
// ======================================================

function timeZoneNamePart(date: Date, timeZone: string, style: string): string {
  const part = formatter(timeZone, {
    timeZoneName: style,
  } as unknown as Intl.DateTimeFormatOptions)
    .formatToParts(date)
    .find((candidate) => candidate.type === 'timeZoneName')
  return part?.value ?? timeZone
}

/** A zone's short name at `date`: EDT, PST, or an offset such as GMT+1 where it has none. */
export function timeZoneAbbreviation(timeZone: string, date: Date = new Date()): string {
  return timeZoneNamePart(date, timeZone, 'short')
}

/** A zone's offset from UTC at `date`, such as GMT−4 or GMT+5:30. */
export function formatTimeZoneOffset(timeZone: string, date: Date = new Date()): string {
  const offset = timeZoneOffsetMinutes(date, timeZone)
  if (offset === 0) return 'GMT'
  const hours = Math.floor(Math.abs(offset) / 60)
  const minutes = Math.abs(offset) % 60
  return `GMT${offset < 0 ? '−' : '+'}${hours}${minutes ? `:${pad(minutes)}` : ''}`
}

// Browsers still name some zones for a city's old name (Chrome's Asia/Calcutta).
const CURRENT_CITY_NAMES: Record<string, string> = {
  Calcutta: 'Kolkata',
  Saigon: 'Ho Chi Minh',
  Katmandu: 'Kathmandu',
  Rangoon: 'Yangon',
  Kiev: 'Kyiv',
  Godthab: 'Nuuk',
  Ulan_Bator: 'Ulaanbaatar',
  Faeroe: 'Faroe',
  Enderbury: 'Kanton',
}

/** The place a zone is named for: `America/Argentina/Buenos_Aires` is Buenos Aires. */
export function timeZoneCity(timeZone: string): string {
  // An alias (US/Eastern) is named for the zone it stands for.
  const name = canonicalTimeZone(timeZone) ?? timeZone
  const last = name.split('/').pop() ?? name
  return (CURRENT_CITY_NAMES[last] ?? last).replace(/_/g, ' ')
}

/** A zone's own name (Eastern Time), or null for one that only reads as an offset. */
function timeZoneGenericName(timeZone: string, date: Date): string | null {
  const generic = timeZoneNamePart(date, timeZone, 'longGeneric')
  return generic === timeZone || /^GMT([+−-]|$)/.test(generic) ? null : generic
}

/** A zone as people say it: Eastern Time (New York), or UTC. */
export function timeZoneLabel(timeZone: string, date: Date = new Date()): string {
  if (isSameTimeZone(timeZone, 'UTC')) return 'Coordinated Universal Time (UTC)'
  const generic = timeZoneGenericName(timeZone, date)
  const city = timeZoneCity(timeZone)
  // Without a name of its own, a zone reads as its offset; its city says more.
  return generic ? `${generic} (${city})` : city
}

/** A zone in a word or two: Eastern Time, its city without a name of its own, or UTC. */
export function timeZoneShortLabel(timeZone: string, date: Date = new Date()): string {
  if (isSameTimeZone(timeZone, 'UTC')) return 'UTC'
  return timeZoneGenericName(timeZone, date) ?? timeZoneCity(timeZone)
}

// ======================================================
// Moments
// ======================================================

/** The zones a moment shows in: the viewer's Primary Time Zone, and where they are when that differs. */
export interface MomentZones {
  timeZone: string
  /** Where the viewer is now, when it isn't `timeZone`; null otherwise. */
  secondaryTimeZone: string | null
}

export function momentZones(displayTimeZone: string, currentTimeZone: string): MomentZones {
  return {
    timeZone: displayTimeZone,
    secondaryTimeZone: isSameTimeZone(displayTimeZone, currentTimeZone) ? null : currentTimeZone,
  }
}

export interface FormattedMoment {
  /** In the viewer's zone, named when a second zone follows: Oct 16, 2026, 9:00 AM EDT. */
  primary: string
  /** Where the viewer is now, when that differs: 6:00 AM PDT (with its date when that differs). */
  secondary: string | null
  /** Both as one line: Oct 16, 2026, 9:00 AM EDT (6:00 AM PDT). */
  text: string
  /** The full date and time in each zone, with the zones' names, for a `<time>`'s title. */
  full: string
  /** The moment as ISO 8601, for a `<time>`'s `dateTime`. */
  iso: string
}

export type MomentStyle = 'datetime' | 'date'

/** A value as a moment, or null: a `Date`, epoch milliseconds, or an ISO string. */
export function parseMoment(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date =
    value instanceof Date
      ? value
      : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : null
  return date && !Number.isNaN(date.getTime()) ? date : null
}

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
}
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' }
const FULL_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'long',
}

function sameDate(date: Date, left: string, right: string) {
  return zonedIsoDate(zonedParts(date, left)) === zonedIsoDate(zonedParts(date, right))
}

/**
 * A moment in the viewer's zone, and also where they are when that differs:
 * "Oct 16, 2026, 9:00 AM EDT (6:00 AM PDT)". Null for a value that isn't one.
 * `date` shows the day only, the second zone's only when it's another day.
 */
export function formatMoment(
  value: unknown,
  zones: MomentZones,
  style: MomentStyle = 'datetime'
): FormattedMoment | null {
  const date = parseMoment(value)
  if (!date) return null
  const { timeZone, secondaryTimeZone } = zones
  const named = secondaryTimeZone !== null
  const primary =
    style === 'date'
      ? formatter(timeZone, DATE_OPTIONS).format(date)
      : formatter(timeZone, {
          ...DATE_OPTIONS,
          ...TIME_OPTIONS,
          ...(named ? { timeZoneName: 'short' } : {}),
        }).format(date)

  let secondary: string | null = null
  if (secondaryTimeZone) {
    const sameDay = sameDate(date, timeZone, secondaryTimeZone)
    if (style === 'datetime') {
      secondary = formatter(secondaryTimeZone, {
        ...(sameDay ? {} : { month: 'short', day: 'numeric' }),
        ...TIME_OPTIONS,
        timeZoneName: 'short',
      }).format(date)
    } else if (!sameDay) {
      secondary = formatter(secondaryTimeZone, { month: 'short', day: 'numeric' }).format(date)
    }
  }

  const full = [timeZone, ...(secondaryTimeZone ? [secondaryTimeZone] : [])]
    .map((zone) => formatter(zone, FULL_OPTIONS).format(date))
    .join('\n')
  return {
    primary,
    secondary,
    text: secondary ? `${primary} (${secondary})` : primary,
    full,
    iso: date.toISOString(),
  }
}

/**
 * A wall-clock time of a definition in `fromTimeZone` (a schedule's 9:00 AM
 * on `isoDate`) as a clock in `toTimeZone` reads it: "6:00 AM PDT", and the
 * day when it's another one ("11:00 PM PDT the day before").
 */
export function formatWallClockElsewhere(
  isoDate: string,
  minutesOfDay: number,
  fromTimeZone: string,
  toTimeZone: string
): string {
  const moment = zonedTimeToDate(isoDate, minutesOfDay, fromTimeZone)
  const time = formatter(toTimeZone, { ...TIME_OPTIONS, timeZoneName: 'short' }).format(moment)
  const there = zonedIsoDate(zonedParts(moment, toTimeZone))
  if (there === isoDate) return time
  return `${time} ${there < isoDate ? 'the day before' : 'the next day'}`
}

/**
 * A time someone picks for a definition, as every selector shows it: on the
 * definition's own clock, named ("9:00 AM EDT"), then the viewer's Primary
 * Time Zone in parentheses when that's another zone ("(6:00 AM PDT)").
 */
export function formatWallClockChoice(
  isoDate: string,
  minutesOfDay: number,
  timeZone: string,
  primaryTimeZone: string
): { label: string; elsewhere: string | null } {
  const moment = zonedTimeToDate(isoDate, minutesOfDay, timeZone)
  const label = formatter(timeZone, { ...TIME_OPTIONS, timeZoneName: 'short' }).format(moment)
  return {
    label,
    elsewhere: isSameTimeZone(timeZone, primaryTimeZone)
      ? null
      : `(${formatWallClockElsewhere(isoDate, minutesOfDay, timeZone, primaryTimeZone)})`,
  }
}
